/**
 * Reading a message off something that was thrown.
 *
 * The shape this replaces annotated the catch binding as `any`. That is not
 * really a type annotation — it is a way of turning the check off: it lets a
 * `.message` access compile no matter what was actually thrown, and
 * JavaScript permits throwing anything, including a string, a plain object,
 * or `undefined`. A rejected fetch, a thrown response body, or a library that
 * rejects with a POJO all arrive here, and reading `.message` off those gives
 * `undefined`, which then renders as the word "undefined" in an error banner.
 *
 * TypeScript types a catch binding as `unknown` by default for that reason.
 * These helpers narrow it once, in one place, instead of at each of the three
 * dozen call sites that were asserting it away.
 */

/** The message from a thrown value, or `fallback` when it carries none. */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'string' && error) return error
  // Some APIs reject with `{ message }` or `{ detail }` rather than an Error.
  if (error && typeof error === 'object') {
    const shape = error as { message?: unknown; detail?: unknown }
    if (typeof shape.message === 'string' && shape.message) return shape.message
    if (typeof shape.detail === 'string' && shape.detail) return shape.detail
  }
  return fallback
}

/** True when a thrown value carries exactly this message — for control flow on a sentinel. */
export function isErrorWithMessage(error: unknown, message: string): boolean {
  return errorMessage(error, '') === message
}

/**
 * True when a thrown value is a fetch abort.
 *
 * Reads `name` directly rather than testing `instanceof Error` first. An
 * AbortError is a `DOMException`, and a DOMException constructed in a
 * different realm — jsdom under vitest, an iframe — fails an `instanceof`
 * check against this realm's `Error`, so the guard silently stops matching
 * and the raw abort propagates in place of the caller's timeout message.
 * Found exactly that way: switching these five call sites to `instanceof`
 * turned four api-client timeout tests red.
 */
export function isAbortError(error: unknown): boolean {
  return (error as { name?: unknown } | null | undefined)?.name === 'AbortError'
}
